import type { TError, TSlugPath } from '@onesource/schemas';
import { queryOptions, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

type SlugPathKind = 'space' | 'lesson' | 'block';

const slugPathQueryKey = (kind: SlugPathKind, id: string) => [ENTITY, 'slug-path', kind, id];

/**
 * Reverse resolution: entity UUID -> canonical slug path. Powers the client-side
 * redirects from legacy `/community-hub/spaces/:id/...` URLs to the new slug URLs.
 */
const slugPathQueryOptions = (kind: SlugPathKind, id: string) =>
  queryOptions<TSlugPath, TError>({
    queryKey: slugPathQueryKey(kind, id),
    queryFn: async () => {
      const res = await axios.get<TSlugPath>(`/community-hub/slug-path/${kind}/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });

export const getSpaceSlugPathQueryOptions = (id: string) => slugPathQueryOptions('space', id);
export const getLessonSlugPathQueryOptions = (id: string) => slugPathQueryOptions('lesson', id);
export const getBlockSlugPathQueryOptions = (id: string) => slugPathQueryOptions('block', id);

export const useSpaceSlugPath = (id: string) => useQuery(getSpaceSlugPathQueryOptions(id));
export const useLessonSlugPath = (id: string) => useQuery(getLessonSlugPathQueryOptions(id));
export const useBlockSlugPath = (id: string) => useQuery(getBlockSlugPathQueryOptions(id));
